import { useState, useEffect } from 'react';
import {
  Box,
  Button,
  Paper,
  Alert,
  useTheme,
  CircularProgress,
  Typography,
  Divider,
  useMediaQuery,
} from '@mui/material';
import { Formik, Form } from 'formik';
import * as Yup from 'yup';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import { visitorService } from '../../../shared/services/visitors/visitorService';
import { eventService } from '../../../shared/services/tenant/eventService';
import { useToast } from '../../../shared/hooks/useToast';
import { useNavigate } from 'react-router-dom';
import { isValidCPF } from '../../../utils/isValidCPF';
import { endOfDay, isAfter, startOfDay } from 'date-fns';
import { CouponField } from './CouponField';
import { TicketSelector, type TicketQuantities } from './TicketSelector';
import { PersonalInfoForm } from './PersonalInfoForm';
import { EventBanner } from './EventBanner';
import { mergeTheme } from '../types';
import { groupRegistrationService } from '../services/groupRegistrationService';
import { MAX_GROUP_SIZE } from '../types/group';
import { trackRegistrationSubmitted } from '../../../shared/analytics/gtm';
import type { TrackableEvent } from '../../../shared/analytics/types';

interface TicketOption {
  type: string;
  label: string;
  value: number;
  description?: string;
  available: number | null;
}

interface MemberFormValues {
  name: string;
  email: string;
  cpf: string;
  phone: string;
  ticket_type: string;
  ticket_value: number;
  ticket_label: string;
  [key: string]: string | number;
}

function getDynamicValidation(fields: any[]) {
  const shape: any = {};
  fields.forEach((field) => {
    let validator;
    switch (field.field_type) {
      case 'email':
        validator = Yup.string().email('E-mail inválido');
        break;
      case 'number':
        validator = Yup.number().typeError('Número inválido');
        break;
      case 'date':
        validator = Yup.string();
        break;
      case 'phone':
        validator = Yup.string();
        break;
      case 'yes_no':
        validator = Yup.string().oneOf(['Sim', 'Não']);
        break;
      case 'select':
        validator = Yup.string();
        break;
      default:
        validator = Yup.string();
    }
    if (field.required) {
      validator = validator.required('Campo obrigatório');
    }
    shape[field.id] = validator;
  });
  return Yup.object().shape(shape);
}

function buildEmptyMember(
  dynamicFields: any[],
  ticket?: TicketOption,
): MemberFormValues {
  const dynamicValues = dynamicFields.reduce((acc: any, field: any) => {
    acc[field.id] = '';
    return acc;
  }, {});

  return {
    name: '',
    email: '',
    cpf: '',
    phone: '',
    ticket_type: ticket?.type || '',
    ticket_value: ticket?.value || 0,
    ticket_label: ticket?.label || '',
    ...dynamicValues,
  };
}

function applyQuantityChange(
  currentMembers: MemberFormValues[],
  type: string,
  quantity: number,
  availableTickets: TicketOption[],
  dynamicFields: any[],
): MemberFormValues[] {
  const ticket = availableTickets.find((t) => t.type === type);
  if (!ticket) return currentMembers;

  const currentCount = currentMembers.filter((member) => member.ticket_type === type).length;
  const delta = quantity - currentCount;

  if (delta > 0) {
    const additions = Array.from({ length: delta }, () =>
      buildEmptyMember(dynamicFields, ticket),
    );
    return [...currentMembers, ...additions];
  }

  if (delta < 0) {
    const toRemove = Math.abs(delta);
    const nextMembers = [...currentMembers];
    let removed = 0;

    for (let i = nextMembers.length - 1; i >= 0 && removed < toRemove; i--) {
      if (nextMembers[i].ticket_type === type) {
        nextMembers.splice(i, 1);
        removed += 1;
      }
    }

    return nextMembers;
  }

  return currentMembers;
}

function buildInitialQuantities(tickets: TicketOption[]): TicketQuantities {
  return tickets.reduce<TicketQuantities>((acc, ticket) => {
    acc[ticket.type] = 0;
    return acc;
  }, {});
}

export function RegisterParticipantForm({
  eventId,
  event,
}: {
  eventId: string;
  event: any;
}) {
  const theme = useTheme();
  const { showToast } = useToast();
  const navigate = useNavigate();

  const isDesktop = useMediaQuery(theme.breakpoints.up('lg'));

  const mergedTheme = mergeTheme(event?.theme_customization);
  const primaryColor = mergedTheme.colors?.primaryColor || '#1565C0';

  const dynamicFields = (event?.dynamicFields || []).slice().sort((a: any, b: any) => {
    if (a?.display_order !== undefined && b?.display_order !== undefined) {
      return a.display_order - b.display_order;
    }
    if (a?.created_at && b?.created_at) {
      return new Date(a.created_at).getTime() - new Date(b.created_at).getTime();
    }
    return 0;
  });

  const basePath = event.slug ? `/evento/${event.slug}` : `/public/event/${eventId}`;

  const [couponData, setCouponData] = useState<{
    code: string;
    discountAmount: number;
    finalValue: number;
  } | null>(null);

  const [availableSlots, setAvailableSlots] = useState<{
    total: { max: number | null; used: number; available: number | null };
    byTicketType: {
      inteiro: { max: number | null; used: number; available: number | null };
      meia: { max: number | null; used: number; available: number | null };
      estudante: { max: number | null; used: number; available: number | null };
      crianca: { max: number | null; used: number; available: number | null };
    };
  } | null>(null);

  useEffect(() => {
    const fetchAvailableSlots = async () => {
      try {
        const response = await eventService.getAvailableSlots(eventId);
        setAvailableSlots(response.data);
      } catch (error) {
        console.error('Erro ao buscar vagas disponíveis:', error);
      }
    };

    if (eventId) {
      fetchAvailableSlots();
    }
  }, [eventId]);

  const isEventOpen = event.status === 'open';
  const registrationDeadline = event.registration_deadline
    ? new Date(
        new Date(event.registration_deadline).getFullYear(),
        new Date(event.registration_deadline).getMonth(),
        new Date(event.registration_deadline).getDate(),
        23,
        59,
        59,
      )
    : null;

  const today = new Date();
  const registrationClosed = registrationDeadline
    ? isAfter(startOfDay(today), endOfDay(registrationDeadline))
    : false;
  const canRegister = isEventOpen && !registrationClosed;

  const availableTickets: TicketOption[] = [
    {
      type: 'inteiro',
      label: event.label_ingresso_inteiro || 'Inscrição Comum',
      description: event.description_ingresso_inteiro || 'Acesso completo à experiência.',
      value: Number(event.valor_ingresso_inteiro) || 0,
      available: availableSlots?.byTicketType.inteiro.available ?? null,
    },
    {
      type: 'meia',
      label: event.label_ingresso_meia || 'Inscrição Meia',
      description: event.description_ingresso_meia || 'Para estudantes e jovens até 18 anos.',
      value: Number(event.valor_ingresso_meia) || 0,
      available: availableSlots?.byTicketType.meia.available ?? null,
    },
    {
      type: 'estudante',
      label: event.label_ingresso_estudante || 'Inscrição Estudante',
      description: event.description_ingresso_estudante || 'Para estudantes com comprovante.',
      value: Number(event.valor_ingresso_estudante) || 0,
      available: availableSlots?.byTicketType.estudante.available ?? null,
    },
    {
      type: 'crianca',
      label: event.label_ingresso_crianca || 'Inscrição Criança',
      description: event.description_ingresso_crianca || 'Para crianças até 12 anos.',
      value: Number(event.valor_ingresso_crianca) || 0,
      available: availableSlots?.byTicketType.crianca.available ?? null,
    },
  ].filter((ticket) => ticket.value > 0);

  const [ticketQuantities, setTicketQuantities] = useState<TicketQuantities>(() =>
    buildInitialQuantities(availableTickets),
  );

  useEffect(() => {
    setTicketQuantities(buildInitialQuantities(availableTickets));
  }, [availableTickets.length]);

  const dynamicValidation = getDynamicValidation(dynamicFields);

  const memberSchema = Yup.object().shape({
    name: Yup.string().required('Nome obrigatório'),
    email: Yup.string().email('E-mail inválido'),
    cpf: Yup.string()
      .required('CPF obrigatório')
      .matches(/^\d{3}\.\d{3}\.\d{3}-\d{2}$/, 'CPF deve estar no formato 999.999.999-99')
      .test('cpf-valid', 'CPF inválido', function (value) {
        if (!value) return false;
        return isValidCPF(value);
      }),
    phone: Yup.string().required('Telefone obrigatório'),
    ticket_type: Yup.string().required('Tipo de ingresso obrigatório'),
    ticket_value: Yup.number().required('Valor obrigatório').min(0, 'Valor inválido'),
    ...dynamicValidation.fields,
  });

  const validationSchema = Yup.object().shape({
    members: Yup.array()
      .of(memberSchema)
      .min(1, 'Selecione pelo menos 1 ingresso')
      .max(MAX_GROUP_SIZE)
      .test(
        'unique-cpfs',
        'Há CPFs repetidos. Cada participante deve ter um CPF diferente.',
        (members) => {
          if (!members) return true;
          const cpfs = members
            .map((member) => member?.cpf?.replace(/\D/g, '') || '')
            .filter((cpf) => cpf.length === 11);
          return new Set(cpfs).size === cpfs.length;
        },
      ),
  });

  const registrationSubtitle =
    event?.registration_subtitle ||
    `Selecione até ${MAX_GROUP_SIZE} ingressos. O primeiro participante é responsável pelo pagamento.`;

  const onSubmit = async (values: { members: MemberFormValues[] }, { setSubmitting }: any) => {
    try {
      const members = values.members;
      const isGroupRegistration = members.length > 1;

      if (isGroupRegistration) {
        const payload = {
          event_id: eventId,
          members: members.map((member) => ({
            name: member.name,
            email: member.email || 'grupos@avivaviagens.com.br',
            cpf: member.cpf.replace(/\D/g, ''),
            phone: member.phone.replace(/\D/g, ''),
            ticket_type: member.ticket_type,
            ticket_value: Number(member.ticket_value),
            dynamicAnswers: dynamicFields.map((field: any) => ({
              dynamic_field_id: field.id,
              answer: String(member[field.id] || ''),
            })),
          })),
          coupon_code: couponData?.code,
        };

        const result = await groupRegistrationService.registerGroup(payload);
        trackRegistrationSubmitted(event as TrackableEvent, {
          value: members.reduce((total, member) => total + Number(member.ticket_value), 0),
          ticketType: members.map((member) => member.ticket_type).join(','),
          isGroupRegistration: true,
          couponCode: couponData?.code,
        });
        showToast('Inscrição registrada com sucesso!', 'success');
        localStorage.setItem('lastCpf', result.principal_cpf);
        navigate(`${basePath}/payment`, { state: { cpf: result.principal_cpf } });
      } else {
        const member = members[0];
        const payload = {
          name: member.name,
          email: member.email || 'grupos@avivaviagens.com.br',
          cpf: member.cpf.replace(/\D/g, ''),
          phone: member.phone.replace(/\D/g, ''),
          ticket_type: member.ticket_type,
          ticket_value: Number(member.ticket_value),
          event_id: eventId,
          dynamicAnswers: dynamicFields.map((field: any) => ({
            dynamic_field_id: field.id,
            answer: member[field.id],
          })),
          coupon_code: couponData?.code,
        };

        await visitorService.register(payload);
        trackRegistrationSubmitted(event as TrackableEvent, {
          value: Number(member.ticket_value),
          ticketType: member.ticket_type,
          isGroupRegistration: false,
          couponCode: couponData?.code,
        });
        showToast('Participante registrado com sucesso!', 'success');
        localStorage.setItem('lastCpf', member.cpf.replace(/\D/g, ''));
        navigate(`${basePath}/payment`, {
          state: {
            cpf: member.cpf.replace(/\D/g, ''),
          },
        });
      }
    } catch (error: any) {
      const backendMessage =
        error?.response?.data?.message ||
        error?.message ||
        'Erro ao registrar participante';
      showToast(backendMessage, 'error');
    }
    setSubmitting(false);
  };

  return (
    <Paper
      sx={{
        p: { xs: 2, md: 3 },
        bgcolor: 'white',
        borderRadius: 2,
        transition: 'all 0.3s cubic-bezier(0.16, 1, 0.3, 1)',
        boxShadow: '0 2px 8px rgba(0, 0, 0, 0.08)',
      }}
    >
      <Formik
        initialValues={{ members: [] as MemberFormValues[] }}
        validationSchema={validationSchema}
        onSubmit={onSubmit}
        enableReinitialize={false}
      >
        {({ values, errors, touched, isSubmitting, setFieldValue, status }) => {
          const members = values.members;
          const isGroupRegistration = members.length > 1;
          const totalValue = members.reduce(
            (sum, member) => sum + (Number(member.ticket_value) || 0),
            0,
          );
          const effectiveTotalValue = couponData ? couponData.finalValue : totalValue;
          const hasTicketsSelected = members.length > 0;
          const membersErrors = errors.members;
          const hasDuplicateCpfs =
            typeof membersErrors === 'string' && membersErrors.includes('CPFs repetidos');

          const handleQuantityChange = (type: string, quantity: number) => {
            const nextQuantities = { ...ticketQuantities, [type]: quantity };
            setTicketQuantities(nextQuantities);
            setCouponData(null);

            const nextMembers = applyQuantityChange(
              members,
              type,
              quantity,
              availableTickets,
              dynamicFields,
            );
            setFieldValue('members', nextMembers);
          };

          return (
            <Form>
              {isDesktop && <EventBanner image={event.image} title={event.title} />}

              <TicketSelector
                tickets={availableTickets}
                quantities={ticketQuantities}
                onQuantityChange={handleQuantityChange}
                eventTitle={event.title}
                totalAvailable={availableSlots?.total.available ?? null}
                themeCustomization={event?.theme_customization}
                registrationTitle={event?.registration_title}
                registrationSubtitle={registrationSubtitle}
                maxTotalQuantity={MAX_GROUP_SIZE}
              />

              {hasTicketsSelected && (
                <Box
                  sx={{
                    animation: 'slideUpFadeIn 0.4s cubic-bezier(0.16, 1, 0.3, 1)',
                    '@keyframes slideUpFadeIn': {
                      '0%': { opacity: 0, transform: 'translateY(20px)' },
                      '100%': { opacity: 1, transform: 'translateY(0)' },
                    },
                  }}
                >
                  <Divider sx={{ my: 3 }} />

                  {members.map((member, index) => {
                    const memberErrors = Array.isArray(membersErrors)
                      ? membersErrors[index]
                      : undefined;
                    const memberTouched = Array.isArray(touched.members)
                      ? touched.members[index]
                      : undefined;
                    const isPrincipal = index === 0;

                    return (
                      <Box
                        key={`${member.ticket_type}-${index}`}
                        sx={{
                          mb: index < members.length - 1 ? 3 : 0,
                          border: '1px solid',
                          borderColor: isPrincipal ? primaryColor : 'divider',
                          borderLeft: '4px solid',
                          borderLeftColor: isPrincipal ? primaryColor : 'divider',
                          borderRadius: 2,
                          overflow: 'hidden',
                        }}
                      >
                        <Box
                          sx={{
                            px: { xs: 2, sm: 3 },
                            py: 1.5,
                            bgcolor: isPrincipal ? `${primaryColor}0d` : '#fafafa',
                            borderBottom: '1px solid',
                            borderColor: isPrincipal ? `${primaryColor}33` : 'divider',
                          }}
                        >
                          <Typography
                            variant="subtitle2"
                            fontWeight={700}
                            sx={{ fontFamily: 'Manrope, sans-serif', color: '#424242' }}
                          >
                            Participante {index + 1}
                            {member.ticket_label ? ` — ${member.ticket_label}` : ''}
                          </Typography>
                          {isPrincipal && isGroupRegistration && (
                            <Typography
                              variant="caption"
                              sx={{ fontFamily: 'Manrope, sans-serif', color: primaryColor, fontWeight: 600 }}
                            >
                              Responsável pelo pagamento
                            </Typography>
                          )}
                        </Box>

                        <Box sx={{ p: { xs: 2, sm: 3 } }}>
                          <PersonalInfoForm
                            values={member}
                            errors={memberErrors || {}}
                            touched={memberTouched || {}}
                            setFieldValue={setFieldValue}
                            isSubmitting={isSubmitting}
                            dynamicFields={dynamicFields}
                            fieldPrefix={`members.${index}`}
                            title={isPrincipal ? 'Complete sua inscrição' : `Dados do participante ${index + 1}`}
                            subtitle={
                              isPrincipal
                                ? 'Utilizaremos estas informações apenas para a sua inscrição, comunicação do evento e confirmação de pagamento.'
                                : 'Preencha os dados deste participante.'
                            }
                            showHeader
                          />
                        </Box>
                      </Box>
                    );
                  })}
                </Box>
              )}

              {hasTicketsSelected && !isGroupRegistration && couponData && couponData.discountAmount > 0 && (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 3, mb: 2 }}>
                  <Typography
                    variant="caption"
                    color="text.secondary"
                    sx={{ textDecoration: 'line-through', fontFamily: 'Manrope, sans-serif' }}
                  >
                    De R$ {totalValue.toFixed(2).replace('.', ',')}
                  </Typography>
                  <Typography
                    variant="caption"
                    color="success.main"
                    fontWeight={600}
                    sx={{ fontFamily: 'Manrope, sans-serif' }}
                  >
                    Economia de R$ {couponData.discountAmount.toFixed(2).replace('.', ',')}
                  </Typography>
                </Box>
              )}

              {hasTicketsSelected && totalValue > 0 && (
                <Box sx={{ mt: 3 }}>
                  <CouponField
                    eventId={eventId}
                    ticketType={members[0]?.ticket_type || ''}
                    ticketValue={totalValue}
                    onCouponApplied={(result) => {
                      setCouponData({
                        code: result.code,
                        discountAmount: result.discountAmount || 0,
                        finalValue: result.finalValue || totalValue,
                      });
                    }}
                    onCouponRemoved={() => setCouponData(null)}
                    themeCustomization={event?.theme_customization}
                    trackingEvent={event as TrackableEvent}
                  />
                </Box>
              )}

              {isGroupRegistration && (
                <>
                  <Divider sx={{ my: 3 }} />
                  <Box
                    sx={{
                      bgcolor: '#f8f9fa',
                      border: '1px solid',
                      borderColor: 'divider',
                      borderRadius: 2,
                      p: 2,
                      mb: 3,
                    }}
                  >
                    <Typography
                      variant="subtitle2"
                      fontWeight={700}
                      sx={{ fontFamily: 'Manrope, sans-serif', mb: 1.5, color: '#424242' }}
                    >
                      Resumo da inscrição
                    </Typography>

                    {members.map((member, index) => (
                      <Box
                        key={index}
                        sx={{
                          display: 'flex',
                          justifyContent: 'space-between',
                          alignItems: 'center',
                          py: 0.75,
                          borderBottom: index < members.length - 1 ? '1px dashed' : 'none',
                          borderColor: 'divider',
                        }}
                      >
                        <Box>
                          <Typography
                            variant="body2"
                            sx={{
                              fontFamily: 'Manrope, sans-serif',
                              fontWeight: index === 0 ? 600 : 400,
                            }}
                          >
                            {member.name || `Participante ${index + 1}`}
                          </Typography>
                          <Typography variant="caption" color="text.secondary" sx={{ fontFamily: 'Manrope, sans-serif' }}>
                            {member.ticket_label}
                          </Typography>
                        </Box>
                        <Typography
                          variant="body2"
                          fontWeight={600}
                          sx={{ fontFamily: 'Manrope, sans-serif', ml: 2, whiteSpace: 'nowrap' }}
                        >
                          R$ {Number(member.ticket_value).toFixed(2).replace('.', ',')}
                        </Typography>
                      </Box>
                    ))}

                    {couponData && couponData.discountAmount > 0 && (
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', pt: 1 }}>
                        <Typography variant="body2" color="success.main" sx={{ fontFamily: 'Manrope, sans-serif' }}>
                          Desconto (cupom)
                        </Typography>
                        <Typography variant="body2" color="success.main" fontWeight={600} sx={{ fontFamily: 'Manrope, sans-serif' }}>
                          − R$ {couponData.discountAmount.toFixed(2).replace('.', ',')}
                        </Typography>
                      </Box>
                    )}

                    <Divider sx={{ my: 1 }} />
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                      <Typography variant="subtitle2" fontWeight={700} sx={{ fontFamily: 'Manrope, sans-serif' }}>
                        Total a pagar
                      </Typography>
                      <Typography variant="subtitle1" fontWeight={700} sx={{ fontFamily: 'Manrope, sans-serif', color: primaryColor }}>
                        R$ {effectiveTotalValue.toFixed(2).replace('.', ',')}
                      </Typography>
                    </Box>
                  </Box>
                </>
              )}

              {hasTicketsSelected && (
                <Box sx={{ mt: 3 }}>
                  <Button
                    type="submit"
                    variant="contained"
                    fullWidth
                    size="large"
                    disabled={isSubmitting || !canRegister}
                    endIcon={
                      !isSubmitting && (
                        <Box
                          sx={{
                            width: 32,
                            height: 32,
                            borderRadius: '50%',
                            bgcolor: '#fff',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            color: '#000',
                          }}
                        >
                          <ArrowForwardIcon sx={{ fontSize: 18 }} />
                        </Box>
                      )
                    }
                    sx={{
                      borderRadius: 4,
                      py: 1.5,
                      px: 2,
                      bgcolor: primaryColor,
                      color: 'white',
                      fontWeight: 600,
                      fontSize: { xs: '0.875rem', md: '1rem' },
                      lineHeight: { xs: '1.2', md: '1.5' },
                      fontFamily: mergedTheme.typography?.fontFamily || 'Manrope, sans-serif',
                      display: 'flex',
                      justifyContent: 'center',
                      alignItems: 'center',
                      position: 'relative',
                      '& .MuiButton-endIcon': {
                        marginLeft: 'auto',
                        marginRight: 0,
                        position: 'absolute',
                        right: 8,
                      },
                      '&:hover': {
                        bgcolor: mergedTheme.colors?.primaryColorHover || primaryColor,
                        opacity: 0.9,
                      },
                      '&:disabled': {
                        bgcolor: 'rgba(0, 0, 0, 0.12)',
                        color: 'rgba(0, 0, 0, 0.26)',
                      },
                    }}
                  >
                    {isSubmitting ? (
                      <>
                        <CircularProgress size={20} sx={{ mr: 1, color: 'white' }} />
                        Confirmando...
                      </>
                    ) : (
                      'Confirmar inscrição e seguir para pagamento'
                    )}
                  </Button>

                  {!isEventOpen && (
                    <Alert severity="error" sx={{ mt: 2 }}>
                      Este evento não está aberto para inscrições.
                    </Alert>
                  )}
                  {registrationClosed && (
                    <Alert severity="error" sx={{ mt: 2 }}>
                      As inscrições para este evento estão encerradas.
                    </Alert>
                  )}
                  {hasDuplicateCpfs && (
                    <Alert severity="error" sx={{ mt: 2 }}>
                      Há CPFs repetidos. Cada participante deve ter um CPF diferente.
                    </Alert>
                  )}
                  {status?.error && (
                    <Alert severity="error" sx={{ mt: 2 }}>
                      {status.error}
                    </Alert>
                  )}
                </Box>
              )}
            </Form>
          );
        }}
      </Formik>
    </Paper>
  );
}
